import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Stethoscope, ArrowRight } from 'lucide-react';

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * "Healed by AI" badge + audit popover for an Intake Hub bill row.
 * Renders only when the document was successfully healed (heal_status === 'healed').
 * Shows the before→after reconciliation gap, score lift, Claude's reasoning and
 * the exact component corrections it applied — so ops can see what changed.
 */
export default function HealedByAiBadge({ heal, status, docId }) {
  if (status !== 'healed' || !heal) return null;

  const corrections = heal.corrections || [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          variant="outline"
          className="rounded-none text-[10px] px-1 py-0 bg-violet-50 text-violet-700 border-violet-200 shrink-0 flex items-center gap-0.5 cursor-pointer hover:bg-violet-100"
          title="Healed by AI — click for details"
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`healed-badge-${docId}`}
        >
          <Stethoscope className="h-2.5 w-2.5" /> Healed by AI
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0 rounded-none"
        data-testid={`healed-popover-${docId}`}
      >
        <div className="px-3 py-2 border-b bg-violet-50 flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-violet-700" />
          <span className="text-sm font-semibold text-violet-900">AI Validation Healer</span>
          {heal.model && (
            <span className="ml-auto text-[10px] text-violet-700/70">{heal.model}</span>
          )}
        </div>

        <div className="p-3 space-y-3">
          {/* Before → After */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Reconciliation gap</div>
              <div className="text-sm font-mono flex items-center gap-1">
                <span className="text-rose-600">{fmtMoney(heal.gap_before)}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-emerald-600 font-semibold">{fmtMoney(heal.gap_after)}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Validation score</div>
              <div className="text-sm font-mono flex items-center gap-1">
                <span className="text-muted-foreground">{heal.score_before ?? '—'}%</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-emerald-600 font-semibold">{heal.score_after ?? '—'}%</span>
              </div>
            </div>
          </div>

          {/* Corrections */}
          {corrections.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Corrections applied</div>
              <div className="space-y-1.5">
                {corrections.map((c, i) => (
                  <div key={i} className="text-xs border rounded-none p-1.5 bg-muted/30" data-testid={`heal-correction-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{(c.field || '').replace(/_/g, ' ')}</span>
                      <span className="font-mono">
                        <span className="text-rose-600">{fmtMoney(c.from)}</span>
                        {' → '}
                        <span className="text-emerald-600">{fmtMoney(c.to)}</span>
                      </span>
                    </div>
                    {c.diagnosis && (
                      <div className="text-[10px] text-violet-700 mt-0.5">{c.diagnosis.replace(/_/g, ' ')}</div>
                    )}
                    {c.evidence && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 italic">“{c.evidence}”</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reasoning */}
          {heal.reason && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Decision</div>
              <p className="text-xs text-foreground">{heal.reason}</p>
            </div>
          )}

          <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t">
            <span>Confidence: {heal.confidence != null ? `${Math.round(heal.confidence * 100)}%` : '—'}</span>
            {heal.at && <span>{new Date(heal.at).toLocaleString()}</span>}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
